import clsx from "clsx";
import { motion } from "framer-motion";
import React, {
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { capitalizeTitle } from "src/helpers/utils";
import Badge from "../Badge/Badge";
import LoadingWithText from "../LoadingWithText/LoadingWithText";
import useUiPreferences from "src/store/useUiPreferences";
import { ICON_SIZES } from "src/constants/icons";

interface ILinksList {
  links: Array<any>;
  rightSide?: ReactNode;
  id?: string;
  sticky?: boolean;
  removeBackground?: boolean;
}

const LinksList: React.FC<ILinksList> = ({
  links,
  rightSide = <></>,
  id,
  sticky = false,
  removeBackground = false,
}) => {
  const miscUiPreferences = useUiPreferences((s) => s.misc);

  // ✅ memo para evitar crear un nuevo array en cada render
  const linksList = useMemo(() => {
    return links?.filter?.(({ hide }) => !hide) ?? [];
  }, [links]);

  const [, startTransition] = useTransition();
  const containerRef = useRef<HTMLUListElement | null>(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [animatedItems, setAnimatedItems] = useState(0);
  const hasScrolledInitially = useRef(false);
  const hasMounted = useRef(false);

  // Esto controla el “subrayado” y color activo visual
  const [visuallyActiveIndices, setVisuallyActiveIndices] = useState<number[]>(
    [],
  );

  const scrollToItem = (index: number, behavior: ScrollBehavior = "smooth") => {
    const item = itemRefs.current[index];
    const container = containerRef.current;

    if (item && container) {
      const isFirst = index === 0;
      const scrollLeft = isFirst
        ? 0
        : item.offsetLeft - container.offsetWidth / 2 + item.offsetWidth / 2;

      container.scrollTo({ left: scrollLeft, behavior });
    }
  };

  // ✅ Scroll inicial al activo (una sola vez cuando ya animó todo)
  useEffect(() => {
    if (
      !hasScrolledInitially.current &&
      animatedItems === linksList.length &&
      linksList.length > 0
    ) {
      const activeIndex = linksList.findIndex((item) => item.active);

      if (activeIndex !== -1) {
        startTransition(() => {
          scrollToItem(activeIndex, "auto");
          requestAnimationFrame(() => {
            setVisuallyActiveIndices((prev) =>
              prev.includes(activeIndex) ? prev : [...prev, activeIndex],
            );
          });
        });
      }

      hasScrolledInitially.current = true;
      hasMounted.current = true;
    }
  }, [animatedItems, linksList, startTransition]);

  // ✅ Mantener visuallyActiveIndices sincronizado con `active` SIN loop infinito
  useEffect(() => {
    if (!hasMounted.current) return;

    const activeNow = linksList
      .map((item, idx) => (item.active ? idx : -1))
      .filter((idx) => idx !== -1);

    setVisuallyActiveIndices((prev) => {
      // Evitar setState si no cambia (previene renders extra)
      const same =
        prev.length === activeNow.length &&
        prev.every((v, i) => v === activeNow[i]);

      if (same) return prev;

      return activeNow;
    });
  }, [linksList]);

  return (
    <div
      className={clsx([
        "-mx-4",
        "md:mx-0",
        "px-4",
        "md:px-0",
        {
          "sticky top-[calc(109px+env(safe-area-inset-top))] z-[10] ":
            sticky && miscUiPreferences.enableMobileTitleBar,
          "sticky top-[calc(0px+env(safe-area-inset-top))] z-[10] ":
            sticky && !miscUiPreferences.enableMobileTitleBar,
          "md:top-[calc(74px+env(safe-area-inset-top))]": sticky,
        },
        !removeBackground && ["bg-white/80", "dark:bg-gray-800/60"],
      ])}
    >
      <div
        className={clsx([
          "flex",
          "w-full",
          "justify-between",
          "items-center",
          "overflow-hidden",
          "px-0",
          "md:pb-1",
          "pb-1",
          "gap-6",
        ])}
        id={id}
      >
        <ul
          ref={containerRef}
          className={clsx([
            "gap-6",
            "md:gap-8",
            "flex",
            "md:w-full",
            "overflow-x-auto",
            "no-scrollbar",
            "w-full",
            "box-content",
            "min-h-[30px]",
            "scroll-smooth",
          ])}
        >
          {linksList.map(
            (
              {
                text,
                onClick,
                active,
                icon,
                isLoading,
                count,
                showCount = false,
              },
              key: number,
            ) => {
              const isVisuallyActive = visuallyActiveIndices.includes(key);

              return (
                <motion.li
                  ref={(el) => (itemRefs.current[key] = el)}
                  className={clsx([
                    "transition-colors",
                    "inline-flex",
                    "flex-row",
                    "md:flex",
                    "items-center",
                    "gap-1",
                    "md:gap-1",
                    "pb-1",
                    "text-xs",
                    "md:text-sm",
                    "cursor-pointer",
                    "relative",
                    "group",
                    "whitespace-nowrap",
                    "md:py-2",
                    "last:border-l-0",
                    isVisuallyActive && "text-emerald-500 dark:text-cyan-400",
                    !isVisuallyActive &&
                      "md:hover:text-emerald-500 md:dark:hover:text-cyan-400",
                  ])}
                  onClick={() => {
                    startTransition(() => {
                      onClick?.();
                      scrollToItem(key, "smooth");
                      setVisuallyActiveIndices((prev) => [
                        ...new Set([...prev, key]),
                      ]);
                    });
                  }}
                  {...(miscUiPreferences.enableComplexAnimations && {
                    initial: { opacity: 0 },
                    animate: { opacity: 1 },
                    transition: {
                      duration: 0.2,
                      ease: "easeOut",
                      delay: key * 0.1,
                      type: "spring",
                      stiffness: 400,
                      damping: 17,
                    },
                  })}
                  onAnimationComplete={() => {
                    startTransition(() => setAnimatedItems((prev) => prev + 1));
                  }}
                  key={key}
                >
                  {isLoading ? (
                    <div
                      className={clsx([
                        `h-[${ICON_SIZES.SM}px]`,
                        `w-[${ICON_SIZES.SM}px]`,
                        "flex",
                        "items-center",
                        "justify-center",
                        "text-emerald-400",
                        "dark:text-cyan-400",
                      ])}
                    >
                      <LoadingWithText size={ICON_SIZES.SM} />
                    </div>
                  ) : (
                    <span
                      className={clsx([
                        !isVisuallyActive && "group-hover:animate-pulse",
                      ])}
                    >
                      {icon}
                    </span>
                  )}

                  <span className={clsx(["font-medium"])}>
                    {capitalizeTitle(text)}
                  </span>

                  {showCount && (
                    <Badge
                      text={count}
                      className={clsx(["text-center", "justify-center"])}
                    />
                  )}

                  <motion.div
                    className={clsx([
                      "absolute",
                      "bottom-0",
                      "h-[3px]",
                      "rounded-full",
                      "bg-emerald-400/70",
                      "dark:bg-cyan-400/70",
                    ])}
                    style={
                      !miscUiPreferences.enableComplexAnimations
                        ? {
                            width: active ? "100%" : 0,
                          }
                        : {}
                    }
                    {...(miscUiPreferences.enableComplexAnimations && {
                      initial: { width: 0 },
                      animate: { width: isVisuallyActive ? "100%" : 0 },
                      whileHover: { width: "100%" },
                      exit: { width: 0 },
                      transition: { duration: 0.3, ease: "easeInOut" },
                    })}
                  />
                </motion.li>
              );
            },
          )}
        </ul>

        {rightSide}
      </div>
    </div>
  );
};

export default React.memo(LinksList);
